import React, { useEffect, useMemo, useState } from "react";
import { auth, db } from "@/lib/firebase";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import LocationConfirmModal from "./LocationConfirmModal";

type Props = {
  // Called when user chooses to use current location (perform geolocation + save, etc.)
  onUseCurrent: () => void;
  // Called when user keeps saved location (no auto-geolocation)
  onKeepSaved: () => void;
};

/**
 * Shows LocationConfirmModal only if the user has NOT set a persistent preference.
 * If preference exists (true or false), modal will not show.
 * Preference path checked (any present wins):
 *  - users/{uid}.preferences.location_auto_on_login (boolean)
 *  - users/{uid}.settings.location.autoOnLogin (boolean)
 */
export default function LocationConfirmGate({ onUseCurrent, onKeepSaved }: Props) {
  const [uid, setUid] = useState<string | null>(auth.currentUser?.uid ?? null);
  const [loaded, setLoaded] = useState(false);
  const [autoPref, setAutoPref] = useState<boolean | undefined>(undefined);

  // Track auth changes
  useEffect(() => {
    const unsub = auth.onAuthStateChanged((u) => {
      setUid(u?.uid ?? null);
    });
    return unsub;
  }, []);

  // Subscribe to user doc to get current preference
  useEffect(() => {
    if (!uid) {
      setLoaded(true);
      setAutoPref(undefined);
      return;
    }
    const ref = doc(db, "users", uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const v = snap.data() as any;
        const a =
          v?.preferences?.location_auto_on_login ??
          v?.settings?.location?.autoOnLogin ??
          undefined;
        setAutoPref(typeof a === "boolean" ? a : undefined);
        setLoaded(true);
      },
      () => {
        setAutoPref(undefined);
        setLoaded(true);
      },
    );
    return unsub;
  }, [uid]);

  // Only prompt when signed in, loaded, and no saved preference exists
  const shouldOpen = useMemo(() => Boolean(uid && loaded && autoPref === undefined), [uid, loaded, autoPref]);

  // Persist the checkbox to the user document
  async function handleToggleRemember(next: boolean) {
    const me = auth.currentUser?.uid;
    if (!me) return;
    const ref = doc(db, "users", me);
    try {
      await updateDoc(ref, {
        "preferences.location_auto_on_login": next,
      });
      setAutoPref(next);
    } catch {
      // ignore write errors; modal can still proceed without persistence
    }
  }

  if (!shouldOpen) return null;

  return (
    <LocationConfirmModal
      open={true}
      onUseCurrent={onUseCurrent}
      onKeepSaved={onKeepSaved}
      onClose={onKeepSaved}
      autoOnLogin={false}
      onToggleAuto={(next) => void handleToggleRemember(next)}
    />
  );
}
